import { capitalize } from "@mui/material";
import { FormikErrors } from "formik";
import { DateTime } from "luxon";
import { SelectOption } from "../../../data/selectInput.type";
import { PrimaryInsured, PurchaseDetails } from "../../purchase/PurchaseData.interface";
import { FullQuoteResult, QuoteData, QuoteResult } from "../QuoteData.interface";
import { config } from '@brand';

const {mtiApiUrl} = config;

export const updatePeopleTab = (insuredArray: Partial<PrimaryInsured>[], formikErrors: FormikErrors<Partial<PrimaryInsured>>[] | undefined): string => {
  let totalPeople = 0;
  let totalMale = 0;
  let totalFemale = 0;
  let invalidPeople = 0;
  
  if (insuredArray && insuredArray.length > 0) {
    for (let insured of insuredArray) {
      totalPeople++;
  
      if (insured.genderObject?.value === 'male') totalMale++;
      if (insured.genderObject?.value === 'female') totalFemale++;
  
      if (insured.hasSpouse) totalPeople++;
      if (insured.spouse?.genderObject?.value === 'male') totalMale++;
      if (insured.spouse?.genderObject?.value === 'female') totalFemale++;
  
      if (insured.hasChildren && insured.children) {
        for (let child of insured.children) {
          totalPeople++;
          if (child.genderObject?.value === 'male') totalMale++;
          if (child.genderObject?.value === 'female') totalFemale++;
        }
      }
    }
  }

  if (formikErrors && formikErrors.length > 0) {
    for (let insured of formikErrors) {
      if (insured?.age || insured?.citizenshipObject || insured?.residencyCountryObject || insured?.residencyCountryObject || insured?.genderObject) {
        invalidPeople++
      }

      if (insured?.spouse) invalidPeople++;

      if (insured?.children) {
        for (let child of insured.children) {
          if (child) invalidPeople++;
        }
      }
    }
  }
  
  const finalCount = totalPeople - invalidPeople;

  if (finalCount === 0) {
    return 'Who needs coverage?';
  } else {
    return `${finalCount} ${finalCount === 1 ? 'Person' : 'People'}`;
  }
}

export const updateDateText = (quoteDate: (Date | string | null), formikErrors: FormikErrors<Date> | undefined, startOrEnd: string): string => {
  let luxonDate;

  if (typeof quoteDate === 'string') {
    luxonDate = DateTime.fromISO(quoteDate as string);
  } else if (quoteDate) {
    luxonDate = DateTime.fromJSDate(quoteDate);
  }

  if (luxonDate && luxonDate.isValid && typeof formikErrors === 'undefined') {
    return luxonDate.toFormat('M/d/yyyy');
  } else {
    return `${startOrEnd} Date`
  }
}

export const updateCoverageAreaText = (usaTrip:string | null, countryObject: SelectOption | null | undefined) => {
  if (usaTrip === 'yes') {
    return 'United States';
  } else {
    return countryObject?.label ? countryObject?.label : 'Where do you need coverage?';
  }
}

export const updateTripProtectionText = (tripProtection:string | undefined | null) => {
  return tripProtection ? `${capitalize(tripProtection)}` : 'Would you like additional coverage?';
}

// IMG answers a failure in the same shape as a price: the quote object, with no
// total, a list of errors and brokenRules as null. Taking that as the quote
// leaves checkout with nothing to charge, so only a priced quote is kept.
export const isPricedQuote = (quote?: Partial<QuoteData> | null): boolean => {
  const errors = quote?.errors as unknown as unknown[] | null | undefined;
  const brokenRules = quote?.brokenRules as unknown[] | null | undefined;

  return !!quote?.productCode
    && typeof quote.totalPremium === 'number'
    && quote.totalPremium > 0
    && !errors?.length
    && !brokenRules?.length;
};

export const quoteProblems = (
  result: { quote?: Partial<QuoteData>, brokenRules?: unknown[] | null } | null
): string[] => {
  const errors = (result?.quote?.errors as unknown as string[] | null) ?? [];
  const rules = ((result?.brokenRules ?? []) as { name?: string }[]).map(rule => rule?.name ?? '');

  return [...errors, ...rules].filter(Boolean);
};

export const submitQuoteForm = async (values: PurchaseDetails, agentNum: string, quote?:QuoteData|null): Promise<FullQuoteResult> => {
  let personCount = 0;
  
  const submittedValues = JSON.parse(JSON.stringify(values)) as PurchaseDetails;
  
  if (submittedValues.initialPaymentDate == '') delete submittedValues.initialPaymentDate;

  if (submittedValues.usaTrip === 'yes') {
    submittedValues.country = 'USA'
  } else {
    submittedValues.country = submittedValues.countryObject?.value ? submittedValues.countryObject.value : '';
  }

  delete submittedValues.countryObject;

  for (let insured of submittedValues.primaryInsured) {
    personCount++;

    insured.age = insured.age === '' ? 0 : insured.age;

    insured.gender = insured.genderObject?.value ? insured.genderObject.value : '';
    insured.citizenship = insured.citizenshipObject?.value ? insured.citizenshipObject.value : '';
    insured.residencyState = insured.residencyStateObject?.value ? insured.residencyStateObject.value : '';
    insured.residencyCountry = insured.residencyCountryObject?.value ? insured.residencyCountryObject.value : '';

    delete insured.genderObject;
    delete insured.citizenshipObject;
    delete insured.residencyStateObject;
    delete insured.residencyCountryObject;

    if (insured.dateOfBirth === null) delete insured.dateOfBirth;

    if (insured.hasSpouse && insured.spouse) {
      personCount++;

      insured.spouse.age = insured.spouse.age === '' ? 0 : insured.spouse.age;

      insured.spouse.gender = insured.spouse.genderObject?.value ? insured.spouse.genderObject.value : '';
      insured.spouse.citizenship = insured.spouse.citizenshipObject?.value ? insured.spouse.citizenshipObject.value : '';
      insured.spouse.residencyState = insured.spouse.residencyStateObject?.value ? insured.spouse.residencyStateObject.value : '';
      insured.spouse.residencyCountry = insured.spouse.residencyCountryObject?.value ? insured.spouse.residencyCountryObject.value : '';

      delete insured.spouse.genderObject;
      delete insured.spouse.citizenshipObject;
      delete insured.spouse.residencyStateObject;
      delete insured.spouse.residencyCountryObject;
      
      if (insured.spouse.dateOfBirth === null) delete insured.spouse.dateOfBirth;

    } else {
      delete insured.spouse;
    }

    if (insured.hasChildren && insured.children) {
      for (let child of insured.children) {
        personCount++;

        child.age = child.age === '' ? 0 : child.age;

        child.gender = child.genderObject?.value ? child.genderObject.value : '';
        child.citizenship = child.citizenshipObject?.value ? child.citizenshipObject.value : '';
        child.residencyState = child.residencyStateObject?.value ? child.residencyStateObject.value : '';
        child.residencyCountry = child.residencyCountryObject?.value ? child.residencyCountryObject.value : '';

        delete child.genderObject;
        delete child.citizenshipObject;
        delete child.residencyStateObject;
        delete child.residencyCountryObject;
        if (child.dateOfBirth === null) delete child.dateOfBirth;

      }
    } else {
      delete insured.children;
    }
  }

  if (submittedValues.useOverallTripCost === 'yes') {
    const perPersonTripCost = (Number(submittedValues.overallTripCost) / personCount).toFixed(2);

    for (let insured of submittedValues.primaryInsured) {
      insured.tripCost = perPersonTripCost;

      if (insured.hasSpouse && insured.spouse) {
        insured.spouse.tripCost = perPersonTripCost;
      }

      if (insured.hasChildren && insured.children) {
        for (let child of insured.children) {
          child.tripCost = perPersonTripCost;
        }
      }
    }
  } else {
    submittedValues.overallTripCost = '';
  }

  const url = quote ? `${mtiApiUrl}/family-quote?agentNum=${agentNum}&appType=${quote?.appType}&productCode=${quote.productCode}` : `${mtiApiUrl}/family-quote?agentNum=${agentNum}`;
  try {
    const request = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(submittedValues),
      headers: {
        'Content-Type': 'application/json'
      }
    })
  
    const data = await request.json();
  
    if (data.error) {
      return {errors: true};
    } else {
      
      const response = data.map(({ quote, product, duration }: QuoteResult) => {
        const { errors = [], brokenRules = [], totalPremium = 0 } = quote;
        const normalizedErrors = errors ?? [];
        const errorsAndBrokenRules = (normalizedErrors).concat(brokenRules);
        const premium = totalPremium ? totalPremium.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        }) : errorsAndBrokenRules.toString();

        return {
          quote,
          product,
          duration,
          premium,
          errors: errorsAndBrokenRules,
          brokenRules:brokenRules
        }
      });
      return {data:response};
    }
  } catch (e: any) {
    return { errors: true };
  }
};

